import { FC } from 'react';

import { Ad } from '@adflow/ad';
import theme from '@adflow/theme';
import type { ElementAnimationStyle } from '@adflow/utils';
import { Box } from '@chakra-ui/react';
import { alpha } from '@mui/material/styles';

import type { EditorCanvasSession } from './useEditorCanvasSession';

const selectionBorderColor = theme.palette.mcBlue.main;
const selectionFillColor = alpha(theme.palette.mcBlue.main, 0.2);
const overlayRadius = '30px';
/** Visual-only: do not include left/top/width/height or the helper trails live drags. */
const overlayAppearanceTransition = 'border-color 0.25s, background-color 0.25s, opacity 0.25s';

export type EditorCanvasStageProps = {
  session: EditorCanvasSession;
  frame: { width: number; height: number };
  stageSize: { width: number; height: number };
  animationOverrides?: ReadonlyMap<string, ElementAnimationStyle> | null;
  testId?: string;
  flex?: number;
};

export const EditorCanvasStage: FC<EditorCanvasStageProps> = ({
  session,
  frame,
  stageSize,
  animationOverrides = null,
  testId = 'editor-canvas-stage',
  flex,
}) => {
  const {
    stageRef,
    viewport,
    displayArtwork,
    selectionBounds,
    selectedItemBounds,
    marqueeBounds,
    hoverBounds,
    isHoveringTarget,
    isPanArmed,
    isPanning,
    clipToFrame,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onPointerLeave,
  } = session;

  const cursor = isPanning
    ? 'grabbing'
    : isPanArmed
    ? 'grab'
    : isHoveringTarget
    ? 'pointer'
    : 'default';

  return (
    <Box
      ref={stageRef}
      data-test-id={testId}
      data-clip-to-frame={clipToFrame ? 'true' : 'false'}
      data-zoom={viewport.zoom}
      data-pan-x={viewport.panX}
      data-pan-y={viewport.panY}
      data-panning={isPanning ? 'true' : 'false'}
      data-pan-armed={isPanArmed ? 'true' : 'false'}
      position="relative"
      flex={flex}
      minH={flex != null ? 0 : undefined}
      width={flex != null ? '100%' : `${stageSize.width}px`}
      height={flex != null ? '100%' : `${stageSize.height}px`}
      overflow="hidden"
      bg="#eef2f6"
      cursor={cursor}
      sx={{ touchAction: 'none', userSelect: 'none' }}
      onDragStart={(event) => event.preventDefault()}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerLeave={onPointerLeave}
    >
      <Box
        position="absolute"
        left={`${viewport.panX}px`}
        top={`${viewport.panY}px`}
        transform={`scale(${viewport.zoom})`}
        transformOrigin="0 0"
      >
        <Ad
          adElements={displayArtwork}
          clipToFrame={clipToFrame}
          isPreview
          animationOverrides={animationOverrides}
        />
        <Box
          data-test-id="editor-canvas-frame"
          position="absolute"
          left={0}
          top={0}
          width={`${frame.width}px`}
          height={`${frame.height}px`}
          border="1px solid"
          borderColor="blackAlpha.500"
          boxShadow="0 0 0 1px rgba(255,255,255,0.7), 0 8px 24px rgba(15, 23, 42, 0.12)"
          pointerEvents="none"
        />
        {hoverBounds ? (
          <Box
            data-test-id="editor-canvas-hover"
            position="absolute"
            left={`${hoverBounds.x}px`}
            top={`${hoverBounds.y}px`}
            width={`${hoverBounds.width}px`}
            height={`${hoverBounds.height}px`}
            pointerEvents="none"
            border="2px solid"
            borderColor={selectionBorderColor}
            borderRadius={overlayRadius}
          />
        ) : null}
        {selectionBounds ? (
          <Box
            data-test-id="editor-canvas-selection"
            position="absolute"
            left={`${selectionBounds.x}px`}
            top={`${selectionBounds.y}px`}
            width={`${selectionBounds.width}px`}
            height={`${selectionBounds.height}px`}
            pointerEvents="none"
            border="2px solid"
            borderColor={selectionBorderColor}
            bg={selectionFillColor}
            borderRadius={overlayRadius}
            transition={overlayAppearanceTransition}
          />
        ) : null}
        {selectedItemBounds.map(({ id, bounds }) => (
          <Box
            key={id}
            data-test-id="editor-canvas-selection-item"
            data-element-id={id}
            position="absolute"
            left={`${bounds.x}px`}
            top={`${bounds.y}px`}
            width={`${bounds.width}px`}
            height={`${bounds.height}px`}
            pointerEvents="none"
            border="1px solid"
            borderColor={selectionBorderColor}
          />
        ))}
        {marqueeBounds ? (
          <Box
            data-test-id="editor-canvas-marquee"
            position="absolute"
            left={`${marqueeBounds.x}px`}
            top={`${marqueeBounds.y}px`}
            width={`${marqueeBounds.width}px`}
            height={`${marqueeBounds.height}px`}
            pointerEvents="none"
            border="1px solid"
            borderColor={selectionBorderColor}
            bg={selectionFillColor}
          />
        ) : null}
      </Box>
    </Box>
  );
};

EditorCanvasStage.displayName = 'EditorCanvasStage';
